// Price Working quantity readers: the stored driver quantity of a cost line,
// shared by Stage 4 editing (src/utils/price-working-edit.js) and the pricing
// ledger. Moved here from price-working-edit.js (Quote Brain slice PC).

import { isPerDayPricingUnit } from './pricing-unit-semantics.js';

const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };

function firstPresent(row, keys, fallback = 0) {
    for (const key of keys) {
        if (row[key] !== undefined && row[key] !== null && row[key] !== '') return num(row[key]);
    }
    return fallback;
}

function workerCount(row) {
    return row.workers == null || row.workers === '' ? 1 : Math.max(0, num(row.workers));
}

export function priceWorkingUnit(row) {
    return String(row?.unit || row?.matchedUnit || row?.matched_unit || '').trim();
}

export function isPerDayPriceWorkingRow(row, unitOverride = null) {
    return isPerDayPricingUnit(
        unitOverride || priceWorkingUnit(row),
        row?.matchedIsDayRate === true
            || row?.matched_is_day_rate === true
            || row?.isDayRate === true
            || row?.is_day_rate === true,
    );
}

function hasStoredDriver(row, keys) {
    return keys.some((key) => (
        Object.prototype.hasOwnProperty.call(row || {}, key)
        && row[key] !== undefined
        && row[key] !== null
        && row[key] !== ''
    ));
}

/**
 * Distinguish a deliberately authored zero from an absent cost driver.
 * @param {Record<string, any>} row
 * @param {string} category
 * @param {string|null} [unitOverride]
 */
export function hasExplicitPriceWorkingDriver(row, category, unitOverride = null) {
    if (!row || typeof row !== 'object') return false;
    const hasQuantity = hasStoredDriver(row, ['qty', 'quantity', 'billableQuantity', 'billable_quantity']);
    if (category === 'materials' || category === 'otherCosts') return hasQuantity;

    const unit = String(unitOverride || priceWorkingUnit(row)).toLowerCase();
    const hasHours = hasStoredDriver(row, ['hours']);
    const hasDuration = hasStoredDriver(row, ['duration_days', 'durationDays']);
    if (category === 'equipment') {
        return isPerDayPriceWorkingRow(row, unitOverride) ? (hasDuration || hasQuantity) : hasQuantity;
    }
    if (category === 'labour' || category === 'subcontractors') {
        if (isPerDayPriceWorkingRow(row, unitOverride)) return hasDuration || hasHours || hasQuantity;
        if (/hour|\bhr\b/.test(unit)) return hasHours;
        return hasQuantity || hasHours;
    }
    return false;
}

export function priceWorkingRowQuantity(row) {
    return firstPresent(row, ['qty', 'quantity', 'billableQuantity', 'billable_quantity']);
}

export function priceWorkingDayDriverValue(row) {
    const durationKeys = ['duration_days', 'durationDays'];
    const durationField = durationKeys.find((key) => (
        Object.prototype.hasOwnProperty.call(row || {}, key)
        && row[key] !== undefined
        && row[key] !== null
        && row[key] !== ''
    ));
    if (durationField) return num(row[durationField]);
    const hours = firstPresent(row || {}, ['hours']);
    if (hours > 0) return hours / 8;
    const quantityField = ['qty', 'quantity'].find((key) => (
        Object.prototype.hasOwnProperty.call(row || {}, key)
        && row[key] !== undefined
        && row[key] !== null
        && row[key] !== ''
    ));
    if (quantityField) return num(row[quantityField]);
    return hours / 8;
}

function timeOrQuantityBillable(row) {
    const unit = priceWorkingUnit(row).toLowerCase();
    const hours = firstPresent(row, ['hours']);
    const quantity = firstPresent(row, ['qty', 'quantity']);
    const hasQuantity = ['qty', 'quantity'].some((key) => (
        Object.prototype.hasOwnProperty.call(row, key)
        && row[key] !== undefined
        && row[key] !== null
        && row[key] !== ''
    ));
    const workers = workerCount(row);
    // Once a stored day driver exists it remains authoritative at zero. Using
    // old hours as a fallback after the builder enters 0 days would silently
    // resurrect a charge the visible editor says was removed.
    if (isPerDayPriceWorkingRow(row)) return priceWorkingDayDriverValue(row) * workers;
    if (/hour|\bhr\b/.test(unit)) return hours * workers;
    // A stored non-time quantity is the canonical driver even when it is
    // deliberately zero. Falling through to stale `hours` in that case turns
    // a zero-quantity row into a charge merely by editing its rate.
    if (hasQuantity) return quantity;
    return hours * workers;
}

/**
 * Canonical stored driver quantity shared by Stage 4 editing and downstream
 * certification. Property presence is authority: an explicit visible zero
 * may never fall through to an older billableQuantity value.
 */
export function priceWorkingBillableQuantity(row, category) {
    if (!row || typeof row !== 'object') return 0;
    if (category === 'labour' || category === 'subcontractors') {
        return timeOrQuantityBillable(row);
    }
    const quantity = priceWorkingRowQuantity(row);
    if (category === 'equipment') {
        const unit = priceWorkingUnit(row).toLowerCase();
        const hasQuantity = ['qty', 'quantity'].some((key) => (
            Object.prototype.hasOwnProperty.call(row, key)
            && row[key] !== undefined
            && row[key] !== null
            && row[key] !== ''
        ));
        const duration = firstPresent(row, ['duration_days', 'durationDays']);
        const hasDuration = ['duration_days', 'durationDays'].some((key) => (
            Object.prototype.hasOwnProperty.call(row, key)
            && row[key] !== undefined
            && row[key] !== null
            && row[key] !== ''
        ));
        const storedBillable = firstPresent(row, ['billableQuantity', 'billable_quantity']);
        return isPerDayPriceWorkingRow(row)
            ? (hasDuration ? (hasQuantity ? quantity : 1) * duration : (hasQuantity ? quantity : storedBillable))
            : (hasQuantity ? quantity : storedBillable);
    }
    return quantity;
}
